<template>
    <div class="room-map-wrapper">
        <LoaderInside v-if="showLoader" />
        <div class="room-map-toolbar mb-3">
            <div class="room-map-toolbar__left">
                <button class="btn btn-edit-mode"
                        :class="{ 'btn-edit-mode--active': isEditMode }"
                        @click="toggleEditMode"
                        v-if="!isEditDisabled">
                    {{ isEditMode ? $t('com.file.room-map.edit-end') : $t('com.file.room-map.edit-start') }}
                </button>
                <button 
                    class="btn btn-primary" 
                    @click="addZone"
                    v-if="isEditMode"
                >
                    + {{ $t('com.file.room-map.add-zone') }}
                </button>
                <button 
                    class="btn btn-primary" 
                    @click="addMicrophone"
                    v-if="isEditMode"
                >
                    + {{ $t('com.file.room-map.add-microphone') || 'Dodaj mikrofon' }}
                </button>
                <div v-if="isEditMode && selectedZoneId" class="zone-color-picker">
                    <label class="zone-color-picker__label">{{ $t('com.file.room-map.back-color') }}:</label>
                    <input 
                        type="color" 
                        class="zone-color-picker__input"
                        :value="selectedZone?.attributes?.backgroundColor || '#ffffff'"
                        @input="updateZoneBackgroundColor($event.target.value)"
                    />
                    <input 
                        type="text" 
                        class="zone-color-picker__hex"
                        :value="selectedZone?.attributes?.backgroundColor || '#ffffff'"
                        @input="updateZoneBackgroundColor($event.target.value)"
                        placeholder="#ffffff"
                    />
                    <label class="zone-color-picker__label">{{ $t('com.file.room-map.border-color') }}:</label>
                    <input 
                        type="color" 
                        class="zone-color-picker__input"
                        :value="selectedZone?.attributes?.borderColor || '#004dcc'"
                        @input="updateZoneBorderColor($event.target.value)"
                    />
                    <input 
                        type="text" 
                        class="zone-color-picker__hex"
                        :value="selectedZone?.attributes?.borderColor || '#004dcc'"
                        @input="updateZoneBorderColor($event.target.value)"
                        placeholder="#004dcc"
                    />
                </div>
            </div>
            <div class="room-map-toolbar__right">
                <button v-if="!isEditMode && !isControlDisabled && sessionStatus === true && votingModule === 1 && getInteractiveVotingModalInAppStatus === false"
                        class="btn btn-voting"
                        @click="startVoting">
                    {{ $t('com.file.agenda-item-context-menu.add-voting') }}
                </button>
            </div>
        </div>
        
        <div 
            class="room-map-container-wrapper"
            ref="mapWrapper"
            :style="{ width: '100%', minWidth: '100%', maxWidth: '100%', height: containerHeight + 'px', overflow: 'auto', border: '2px solid #dce4ee', borderRadius: '8px', backgroundColor: '#f8f9fa', boxSizing: 'border-box' }"
        >
            
            <div 
                class="room-map-container" 
                ref="mapContainer"
                @click="handleContainerClick"
                :style="{ width: containerWidth + 'px', minWidth: containerWidth + 'px', height: containerHeight + 'px', position: 'relative', boxSizing: 'border-box' }"
            >
            <template v-if="zonesVisible">
                <Vue3DraggableResizable
                    v-for="zone in zones"
                    :key="`zone-${zone.id}-${containerKey}`"
                    :x="zone.x"
                    :y="zone.y"
                    :w="zone.w"
                    :h="zone.h"
                    :minW="100"
                    :minH="60"
                    :resizable="isEditMode"
                    :draggable="isEditMode"
                    :parent="false"
                    :parentW="containerWidth || 1200"
                    :parentH="containerHeight || 675"
                    @dragging="(event) => onDrag(zone.id, event)"
                    @resizing="(event) => onResize(zone.id, event)"
                    @dragstop="(event) => onDragStop(zone.id, event)"
                    @resizestop="(event) => onResizeStop(zone.id, event)"
                    @drag-end="(event) => onDragStop(zone.id, event)"
                    @resize-end="(event) => onResizeStop(zone.id, event)"
                    @dragstart="(event) => onDragStart(zone.id, event)"
                    @resizestart="(event) => onResizeStart(zone.id, event)"
                    @clicked="(event) => handleZoneClick(zone.id, event)"
                    @mousedown="(event) => handleZoneMouseDown(zone.id, event)"
                    :class="{ 'zone-selected': selectedZoneId === zone.id, 'zone-edit-mode': isEditMode }"
                >
                <div class="zone-content"  @click.stop="selectZone(zone.id)"
                    :style="{ backgroundColor: zone.attributes?.backgroundColor || '#ffffff', borderColor: zone.attributes?.borderColor || '#004dcc'}">
                    <div class="zone-header">
                        <span style="flex: 1;"></span>
                        <button class="btn-close-zone" @click.stop="removeZone(zone.id)" v-if="selectedZoneId === zone.id && isEditMode">×</button>
                    </div>
                    <div class="zone-name" v-if="editingZoneId !== zone.id" @dblclick.stop="isEditMode && startEditZone(zone.id)" @click.stop="selectZone(zone.id)"
                        :class="{ 'zone-name-editable': isEditMode }">
                        {{ zone.name }}
                    </div>
                    <input
                        v-else
                        class="zone-name-input"
                        v-model="zone.name"
                        @blur="stopEditZone"
                        @keyup.enter="stopEditZone"
                        @keyup.esc="cancelEditZone"
                        ref="zoneNameInput"
                        @click.stop
                    />
                </div>
            </Vue3DraggableResizable>
            </template>

            <template v-if="microphonesVisible">
                <Vue3DraggableResizable
                    v-for="mic in microphones"
                :key="`mic-${mic.id}-${containerKey}`"
                :x="mic.x"
                :y="mic.y"
                :w="microphoneSize.w"
                :h="microphoneSize.h"
                :resizable="false"
                :draggable="isEditMode"
                :parent="false"
                :parentW="containerWidth || 1200"
                :parentH="containerHeight || 675"
                @dragging="(event) => onMicrophoneDrag(mic.id, event)"
                @dragstop="(event) => onMicrophoneDragStop(mic.id, event)"
                @drag-end="(event) => onMicrophoneDragStop(mic.id, event)"
                @dragstart="(event) => onMicrophoneDragStart(mic.id, event)"
                @clicked="() => isEditMode && selectMicrophone(mic.id)"
                @mousedown="() => isEditMode && selectMicrophone(mic.id)"
                :class="{ 'mic-selected': selectedMicrophoneId === mic.id }"
            >
                <div class="mic-on-map" :class="{ 'mic-on-map--edit': isEditMode }" @click.stop>
                    <button
                        class="mic-on-map__remove"
                        v-if="isEditMode && selectedMicrophoneId === mic.id"
                        @click.stop="removeMicrophone(mic.id)"
                    >×</button>
                    <div 
                        class="mic-on-map__icon" 
                        :class="{
                            'mic-on-map__icon--speaking': getMicrophoneStatus(mic.id) === 'speaking',
                            'mic-on-map__icon--queued': getMicrophoneStatus(mic.id) === 'queued',
                            'mic-on-map__icon--inactive': getMicrophoneStatus(mic.id) === null && !isEditMode,
                            'mic-on-map__icon--clickable': !isEditMode && getMicrophoneUsers(mic.id).length > 0
                        }"
                        @click="handleMicrophoneClick(mic.id, $event)"
                    >
                        <img src="@/assets/icons/components/microphone.svg" :alt="$t('com.file.discussion-quest-item.give-voice')" />
                        <div
                            class="mic-on-map__number"
                            v-if="editingMicrophoneId !== mic.id"
                            @dblclick.stop="isEditMode && startEditMicrophoneNumber(mic.id)"
                        >
                            {{ mic.number }}
                        </div>
                        <input
                            v-else
                            :ref="'mic-input-' + mic.id"
                            class="mic-on-map__number-input"
                            type="number"
                            min="1"
                            v-model.number="mic.number"
                            @blur="saveMicrophoneNumber(mic.id)"
                            @keyup.enter="saveMicrophoneNumber(mic.id)"
                            @keyup.esc="cancelEditMicrophoneNumber(true)"
                        />
                    </div>
                    <div class="mic-on-map__users" v-if="!isEditMode || editingMicrophoneId !== mic.id">
                        <div 
                            v-if="getMicrophoneUsers(mic.id).length === 1"
                            class="mic-on-map__user-name"
                            :class="{ 
                                'mic-on-map__user-name--single': !getMicrophoneUsers(mic.id)[0].name || !getMicrophoneUsers(mic.id)[0].lastName,
                                'mic-on-map__user-name--speaking': getMicrophoneStatus(mic.id) === 'speaking',
                                'mic-on-map__user-name--queued': getMicrophoneStatus(mic.id) === 'queued',
                                'mic-on-map__user-name--inactive': getMicrophoneStatus(mic.id) === null && !isEditMode,
                                'mic-on-map__user-name--clickable': !isEditMode
                            }"
                            @click="handleMicrophoneClick(mic.id, $event)"
                        >
                            <span class="mic-on-map__user-name-first" v-if="getMicrophoneUsers(mic.id)[0].name">{{ getMicrophoneUsers(mic.id)[0].name }}</span>
                            <span class="mic-on-map__user-name-last" v-if="getMicrophoneUsers(mic.id)[0].lastName">{{ getMicrophoneUsers(mic.id)[0].lastName }}</span>
                        </div>
                        <div 
                            v-else-if="getMicrophoneUsers(mic.id).length > 1"
                            class="mic-on-map__user-count"
                            :class="{
                                'mic-on-map__user-count--speaking': getMicrophoneStatus(mic.id) === 'speaking',
                                'mic-on-map__user-count--queued': getMicrophoneStatus(mic.id) === 'queued',
                                'mic-on-map__user-count--inactive': getMicrophoneStatus(mic.id) === null && !isEditMode,
                                'mic-on-map__user-count--clickable': !isEditMode
                            }"
                            @click="handleMicrophoneClick(mic.id, $event)"
                        >
                            {{ getMicrophoneUsers(mic.id).length }}
                        </div>
                    </div>
                    <!-- Modal z listą użytkowników przypisanych do mikrofonu -->
                    <div 
                        v-if="!isEditMode && expandedMicrophoneId === mic.id && getMicrophoneUsers(mic.id).length > 0"
                        class="mic-on-map__users-list"
                        :class="{
                            'mic-on-map__users-list--single': getMicrophoneUsers(mic.id).length === 1,
                            'mic-on-map__users-list--multiple': getMicrophoneUsers(mic.id).length > 1
                        }"
                        @click.stop
                    >
                        <div 
                            v-for="user in getMicrophoneUsers(mic.id)"
                            :key="user.id"
                            class="mic-on-map__users-list-item-wrapper"
                        >
                            <div 
                                class="mic-on-map__users-list-item"
                                :class="{
                                    'mic-on-map__users-list-item--queued': isUserInQueue(user.id),
                                    'mic-on-map__users-list-item--speaking': isUserSpeaking(user.id)
                                }"
                                @click="handleUserFromMicrophoneClick(user.id, $event)"
                            >
                                <span class="mic-on-map__users-list-item-name">{{ user.name }} {{ user.lastName }}</span>
                                <div class="mic-on-map__users-list-item-actions">
                                    <button 
                                        class="mic-on-map__users-list-item-action-btn mic-on-map__users-list-item-action-btn--av"
                                        @click.stop="handleUserActionClick(user.id, 3, 1, $event)"
                                    >
                                        {{ $t('com.file.discussion-quest-item.av') }}
                                    </button>
                                    <button 
                                        class="mic-on-map__users-list-item-action-btn mic-on-map__users-list-item-action-btn--wf"
                                        @click.stop="handleUserActionClick(user.id, 4, 1, $event)"
                                    >
                                        {{ $t('com.file.discussion-quest-item.wf') }}
                                    </button>
                                    <div 
                                        class="mic-on-map__users-list-item-voice-icon"
                                        :class="{
                                            'mic-on-map__users-list-item-voice-icon--active': isUserSpeaking(user.id)
                                        }"
                                        @click.stop="toggleUserVoice(user.id, $event)"
                                    >
                                        <img src="@/assets/icons/components/microphone.svg" alt="Głos" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Vue3DraggableResizable>
            </template>
            </div>
        </div>
        <div class="room-map-controls">
            <label class="room-map-controls__checkbox">
                <input 
                    type="checkbox" 
                    v-model="zonesVisible"
                    class="room-map-controls__checkbox-input"
                />
                <span class="room-map-controls__checkbox-label">{{ $t('com.file.room-map.zones-visible') || 'Strefy widoczne' }}</span>
            </label>
            <label class="room-map-controls__checkbox">
                <input 
                    type="checkbox" 
                    v-model="microphonesVisible"
                    class="room-map-controls__checkbox-input"
                />
                <span class="room-map-controls__checkbox-label">{{ $t('com.file.room-map.microphones-visible') || 'Mikrofony widoczne' }}</span>
            </label>
        </div>

    </div>
</template>

<script>
	import Vue3DraggableResizable from 'vue3-draggable-resizable'
    import AlertHelper from '@/helpers/alerts.helper.js';
    import roomMapService from '@/services/room.map.service';
    import LoaderInside from "@/components/BasicControls/LoaderInside.vue";

export default {
    name: 'RoomMap',
    components: {
        Vue3DraggableResizable,
        LoaderInside
    },
    props: {
        isControlDisabled: {
            type: Boolean,
            default: false
        },
        isEditDisabled: {
            type: Boolean,
            default: false
        },
        votingModule: {
            type: Number,
            default: 0
        },
        roomMapId: {
            type: [String, Number],
            default: null
        },
        currentSpeech: {
            type: Object,
            default: null
        },
        discussRequestList: {
            type: Array,
            default: () => []
        },
        activePoint: {
            type: Object,
            default: null
        },
        sessionStatus: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            zones: [],
            selectedZoneId: null,
            editingZoneId: null,
            microphones: [],
            selectedMicrophoneId: null,
            editingMicrophoneId: null,
            micOriginalNumber: null,
            microphoneUsers: {}, // Map micId -> array of users
            userDiscussionIds: {}, // Map userId -> discussionId
            allUsers: [], // Cache listy użytkowników dla szybszej aktualizacji par mic-user
            expandedMicrophoneId: null, // ID mikrofonu z rozwiniętą listą użytkowników
            containerHeight: 675,
            containerWidth: 1200,
            originalZoneName: '',
            resizeObserver: null,
            isEditMode: false,
            zonesVisible: true,
            microphonesVisible: true,
            dragScrollInterval: null,
            isDragging: false,
            dragScrollMouseMoveHandler: null,
            pendingZoneUpdate: null,
            pendingMicrophoneUpdate: null,
            isDraggingZone: false,
            isResizingZone: false,
            isDraggingMicrophone: false,
            tempZoneId: 1,
            tempMicrophoneId: -1,
            microphoneSize: {
                w: 70,
                h: 90
            },
            showLoader: false,
        }
    },
    computed: {
        selectedZone() {
            if (!this.selectedZoneId) return null
            return this.zones.find(z => z.id === this.selectedZoneId)
        },
        // Computed property do wymuszenia aktualizacji komponentów przy zmianie rozmiaru
        containerKey() {
            return `${this.containerWidth}-${this.containerHeight}`
        },
        getInteractiveVotingModalInAppStatus() {
            return this.$store.state.votingStore.interactiveVotingModalInAppStatus;
        }
    },
    watch: {
        roomMapId: {
            immediate: true,
            async handler(newVal) {
                if (newVal) {
                    await this.loadZones()
                    await this.loadMicrophones()
                }
            }
        },
        async containerWidth() {
            await this.$nextTick()
            this.$forceUpdate()
        },
        async containerHeight() {
            await this.$nextTick()
            this.$forceUpdate()
        },
        discussRequestList: {
            handler() {
                this.updateUserDiscussionIds()
            },
            deep: true
        },
        currentSpeech: {
            handler() {
                this.updateUserDiscussionIds()
            },
            deep: true
        },
        isEditDisabled: {
            handler(newVal) {
                // Automatycznie wyłącz tryb edycji, jeśli edycja została zablokowana
                if (newVal && this.isEditMode) {
                    this.isEditMode = false
                    this.selectedZoneId = null
                    this.stopEditZone()
                    this.selectedMicrophoneId = null
                    this.cancelEditMicrophoneNumber(false)
                    this.$emit('microphone-selected', null)
                    this.$emit('edit-mode-changed', false)
                }
            },
            immediate: true
        }
    },
    async mounted() {
        this.updateContainerHeight();
        
        this.updateUserDiscussionIds();

        await this.$nextTick();

        this.updateContainerHeight();

        window.addEventListener('resize', this.updateContainerHeight);
        window.addEventListener('mouseup', this.handleGlobalMouseUp);

        // ResizeObserver
        const root = this.$el
        const wrapper = root?.querySelector('.room-map-container-wrapper')
        const container = root?.querySelector('.room-map-container')

        if (wrapper) {
            this.resizeObserver = new ResizeObserver(async () => {
                await this.$nextTick()
                this.updateContainerHeight()
            })

            this.resizeObserver.observe(wrapper)
            if (container) this.resizeObserver.observe(container)
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateContainerHeight)
        window.removeEventListener('mouseup', this.handleGlobalMouseUp)
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
        // Zatrzymaj automatyczne scrollowanie, jeśli jest aktywne
        this.stopDragScroll()
    },
    methods: {
        async loadZones() {
            if (!this.roomMapId) {
                console.warn('roomMapId nie jest ustawione')
                return
            }
            try {
                const response = await roomMapService.getZones(this.roomMapId)
                if (response.status === 200 && response.data) {
                    this.zones = response.data.map(zone => {
                        // Obsługa migracji ze starej struktury (bez attributes)
                        if (!zone.attributes) {
                            zone.attributes = {
                                backgroundColor: zone.backgroundColor || '#ffffff',
                                borderColor: zone.borderColor || '#004dcc'
                            }
                        }
                        return zone
                    })

                    await this.$nextTick();
                    this.updateContainerHeight();

                }
            } catch (error) {
                console.error('Błąd podczas pobierania stref:', error)
            }
        },
        async loadMicrophones() {
            if (!this.roomMapId) {
                console.warn('roomMapId nie jest ustawione')
                return
            }
            try {
                const response = await roomMapService.getMicrophones(this.roomMapId)
                if (response.status === 200 && Array.isArray(response.data)) {
                    this.microphones = response.data.map(mic => ({
                        id: mic.id,
                        number: mic.number ?? mic.Number ?? 0,
                        x: mic.x ?? mic.X ?? 0,
                        y: mic.y ?? mic.Y ?? 0
                    }))
                    // Załaduj przypisanych użytkowników dla każdego mikrofonu
                    await this.loadMicrophoneUsers()
                }
            } catch (error) {
                console.error('Błąd podczas pobierania mikrofonów:', error)
            }
        },
        async loadMicrophoneUsers() {
            if (!this.roomMapId) return
            this.microphoneUsers = {}
            
            // Pobierz listę wszystkich dostępnych użytkowników
            let allUsers = []
            try {
                const usersResponse = await roomMapService.getUsersForMicrophones(this.roomMapId)
                if (usersResponse.status === 200 && Array.isArray(usersResponse.data)) {
                    allUsers = usersResponse.data.map(user => ({
                        id: user.id || user.Id,
                        name: user.name || user.Name || '',
                        lastName: user.lastName || user.LastName || '',
                        function: user.function || user.Function || ''
                    }))
                    this.allUsers = allUsers
                }
            } catch (error) {
                console.error('Błąd podczas pobierania listy użytkowników:', error)
            }
            
            // Pobierz wszystkie przypisania na raz
            try {
                const pairsResponse = await roomMapService.getAllMicrophoneUsers(this.roomMapId)
                if (pairsResponse.status === 200 && Array.isArray(pairsResponse.data)) {
                    // response.data to lista wszystkich par { MicId, Lid }
                    const pairs = pairsResponse.data
                    
                    // Zmapuj pary na strukturę microphoneUsers[micId] = [users]
                    pairs.forEach(pair => {
                        const micId = pair.micId || pair.MicId
                        const userId = pair.lid || pair.Lid
                        
                        if (micId != null && userId != null) {
                            // Znajdź użytkownika po ID
                            const user = allUsers.find(u => u.id === userId || u.id === parseInt(userId))
                            
                            if (user) {
                                // Inicjalizuj tablicę dla mikrofonu jeśli nie istnieje
                                if (!this.microphoneUsers[micId]) {
                                    this.microphoneUsers[micId] = []
                                }
                                
                                // Dodaj użytkownika jeśli jeszcze go nie ma
                                if (!this.microphoneUsers[micId].some(u => u.id === user.id)) {
                                    this.microphoneUsers[micId].push(user)
                                }
                            }
                        }
                    })
                }
            } catch (error) {
                console.error('Błąd podczas pobierania przypisań użytkowników do mikrofonów:', error)
            }
        },
        getMicrophoneUsers(micId) {
            return this.microphoneUsers[micId] || []
        },
        applyMicUserPairs(pairs) {
            if (!Array.isArray(pairs)) return
            // Jeśli nie mamy listy użytkowników, spróbuj załadować i odłóż aktualizację
            if (!this.allUsers || this.allUsers.length === 0) {
                this.loadMicrophoneUsers()
                return
            }
            const newMap = {}
            pairs.forEach(pair => {
                const micId = pair.micId || pair.MicId
                const userIdRaw = pair.lid || pair.Lid
                if (micId == null || userIdRaw == null) return
                const userId = typeof userIdRaw === 'number' ? userIdRaw : parseInt(userIdRaw)
                const user = this.allUsers.find(u => {
                    const uid = typeof u.id === 'number' ? u.id : parseInt(u.id)
                    return uid === userId
                })
                if (!user) return
                if (!newMap[micId]) {
                    newMap[micId] = []
                }
                // unikaj duplikatów w newMap
                if (!newMap[micId].some(u => u.id === user.id)) {
                    newMap[micId].push(user)
                }
            })
            this.microphoneUsers = newMap
        },
        updateUserDiscussionIds() {
            // Reset mapowania
            this.userDiscussionIds = {}
            
            // Mapuj discussionId z discussRequestList
            if (this.discussRequestList && Array.isArray(this.discussRequestList)) {
                this.discussRequestList.forEach(request => {
                    if (request.userId && request.discussionId) {
                        const userId = typeof request.userId === 'number' ? request.userId : parseInt(request.userId)
                        const discussionId = typeof request.discussionId === 'number' ? request.discussionId : parseInt(request.discussionId)
                        this.userDiscussionIds[userId] = discussionId
                    }
                })
            }
            
            // Mapuj discussionId z currentSpeech
            if (this.currentSpeech && this.currentSpeech.userId && this.currentSpeech.discussionId) {
                const userId = typeof this.currentSpeech.userId === 'number' ? this.currentSpeech.userId : parseInt(this.currentSpeech.userId)
                const discussionId = typeof this.currentSpeech.discussionId === 'number' ? this.currentSpeech.discussionId : parseInt(this.currentSpeech.discussionId)
                this.userDiscussionIds[userId] = discussionId
            }
        },
        getUserDiscussionId(userId) {
            const id = typeof userId === 'number' ? userId : parseInt(userId)
            return this.userDiscussionIds[id] || null
        },
        isUserInQueue(userId) {
            if (!this.discussRequestList || !Array.isArray(this.discussRequestList)) return false
            const id = typeof userId === 'number' ? userId : parseInt(userId)
            return this.discussRequestList.some(request => {
                const requestUserId = typeof request.userId === 'number' ? request.userId : parseInt(request.userId)
                return requestUserId === id
            })
        },
        isUserSpeaking(userId) {
            if (!this.currentSpeech) return false
            const id = typeof userId === 'number' ? userId : parseInt(userId)
            const speechUserId = typeof this.currentSpeech.userId === 'number' 
                ? this.currentSpeech.userId 
                : parseInt(this.currentSpeech.userId)
            return id === speechUserId
        },
        toggleUserVoice(userId, event) {
            if (this.isEditMode) return
            if (this.isControlDisabled) return // Nie można zarządzać dyskusją gdy sessionId = 0
            if (event) event.stopPropagation()
            
            const id = typeof userId === 'number' ? userId : parseInt(userId)
            
            if (this.isUserSpeaking(id)) {
                // Użytkownik ma głos - zabierz go
                this.$emit('voice-revoked', id)
            } else {
                // Użytkownik nie ma głosu - udziel go
                this.$emit('voice-granted', id)
            }
        },
        handleUserActionClick(userId, type, handle, event) {
            if (this.isEditMode) return
            if (this.isControlDisabled) return // Nie można zarządzać dyskusją gdy sessionId = 0
            if (event) event.stopPropagation()
            
            const id = typeof userId === 'number' ? userId : parseInt(userId)
            this.$emit('user-queued', id, type, handle)
        },
        handleMicrophoneClick(micId, event) {
            if (this.isEditMode) return
            if (this.isControlDisabled) return // Nie można zarządzać dyskusją gdy sessionId = 0
            if (event) event.stopPropagation()
            
            const users = this.getMicrophoneUsers(micId)
            if (users.length === 0) return
            
            // Rozwinięcie/zwiniecie listy (dla 1 lub więcej użytkowników)
            if (this.expandedMicrophoneId === micId) {
                this.expandedMicrophoneId = null
            } else {
                this.expandedMicrophoneId = micId
            }
        },
        handleUserFromMicrophoneClick(userId, event) {
            if (this.isEditMode) return
            if (this.isControlDisabled) return // Nie można zarządzać dyskusją gdy sessionId = 0
            if (event) event.stopPropagation()
            
            this.toggleUserQueue(userId)
        },
        toggleUserQueue(userId) {
            if (this.isControlDisabled) return // Nie można zarządzać dyskusją gdy sessionId = 0
            const id = typeof userId === 'number' ? userId : parseInt(userId)
            
            if (this.isUserInQueue(id)) {
                // Użytkownik jest w kolejce - usuń go
                this.$emit('user-removed-from-queue', id)
            } else {
                // Użytkownik nie jest w kolejce - dodaj go (typ = 2, handle = 1)
                this.$emit('user-queued', id, 2, 1)
            }
        },
        getMicrophone(micId) {
            return this.microphones.find(m => m.id === micId)
        },
        getMicrophoneStatus(micId) {
            // Zwraca status mikrofonu: 'speaking', 'queued', lub null
            if (this.isEditMode) return null
            
            // Sprawdź czy mikrofon ma głos (po microphoneId lub userId)
            if (this.currentSpeech) {
                // Sprawdź po microphoneId (jeśli jest dostępne)
                if (this.currentSpeech.microphoneId != null) {
                    const speechMicId = typeof this.currentSpeech.microphoneId === 'number' 
                        ? this.currentSpeech.microphoneId 
                        : parseInt(this.currentSpeech.microphoneId)
                    const compareMicId = typeof micId === 'number' ? micId : parseInt(micId)
                    if (speechMicId === compareMicId) {
                        return 'speaking'
                    }
                }
                
                // Sprawdź po userId (jeśli użytkownik przypisany do mikrofonu ma głos)
                const micUsers = this.getMicrophoneUsers(micId)
                if (micUsers.length > 0) {
                    const hasUserSpeaking = micUsers.some(user => {
                        const userId = typeof user.id === 'number' ? user.id : parseInt(user.id)
                        const speechUserId = typeof this.currentSpeech.userId === 'number' 
                            ? this.currentSpeech.userId 
                            : parseInt(this.currentSpeech.userId)
                        return userId === speechUserId
                    })
                    if (hasUserSpeaking) {
                        return 'speaking'
                    }
                }
            }
            
            // Sprawdź czy któryś z użytkowników przypisanych do mikrofonu jest w kolejce
            const micUsers = this.getMicrophoneUsers(micId)
            if (micUsers.length > 0 && this.discussRequestList && this.discussRequestList.length > 0) {
                const hasUserQueued = micUsers.some(user => {
                    const userId = typeof user.id === 'number' ? user.id : parseInt(user.id)
                    return this.discussRequestList.some(request => {
                        const requestUserId = typeof request.userId === 'number' 
                            ? request.userId 
                            : parseInt(request.userId)
                        return userId === requestUserId
                    })
                })
                if (hasUserQueued) {
                    return 'queued'
                }
            }
            
            return null
        },
        async assignUserToMicrophone(micId, userId) {
            if (!this.roomMapId || !this.isEditMode) return
            
            // Sprawdź czy użytkownik jest już przypisany do innego mikrofonu
            let previousMicId = null
            for (const [otherMicId, users] of Object.entries(this.microphoneUsers)) {
                if (parseInt(otherMicId) !== micId && users.some(u => u.id === userId)) {
                    previousMicId = parseInt(otherMicId)
                    break
                }
            }
            
            try {
                this.showLoader = true;
                // Usuń z poprzedniego mikrofonu jeśli był przypisany
                if (previousMicId) {
                    await roomMapService.removeUserFromMicrophone(this.roomMapId, previousMicId, userId)
                }

                // Dodaj do nowego mikrofonu
                await roomMapService.assignUserToMicrophone(this.roomMapId, micId, userId)

                // Pobierz zaktualizowaną listę wszystkich przypisań z backendu
                await this.loadMicrophoneUsers()

                // Emituj event do parenta, aby zaktualizować listę użytkowników
                this.$emit('microphone-users-changed', { micId, userId })
            } catch (error) {
                console.error('Błąd podczas przypisywania użytkownika do mikrofonu:', error)
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
            }
            finally {
                this.showLoader = false;
            }
        },
        async removeUserFromMicrophone(micId, userId) {
            if (!this.roomMapId || !this.isEditMode) return
            
            try {
                this.showLoader = true;
                await roomMapService.removeUserFromMicrophone(this.roomMapId, micId, userId)

                // Pobierz zaktualizowaną listę wszystkich przypisań z backendu
                await this.loadMicrophoneUsers()

                // Emituj event do parenta, aby zaktualizować listę użytkowników
                this.$emit('microphone-users-changed', { micId, userId })
            } catch (error) {
                console.error('Błąd podczas usuwania użytkownika z mikrofonu:', error)
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
            }
            finally {
                this.showLoader = false;
            }
        },
        updateContainerHeight() {
            // Ustaw wysokość kontenera na podstawie dostępnej przestrzeni w oknie przeglądarki
            const wrapper = this.$el?.querySelector('.room-map-container-wrapper')
            if (wrapper) {
                // Użyj getBoundingClientRect dla dokładniejszego pomiaru
                const rect = wrapper.getBoundingClientRect()
                const visibleWidth = rect.width || wrapper.clientWidth || wrapper.offsetWidth
                
                // Oblicz dostępną wysokość okna przeglądarki
                const viewportHeight = window.innerHeight
                const headerHeight = 140 // Wysokość headera
                const toolbarHeight = 60 // Wysokość toolbara
                const containerMargin = 98 // Marginesy kontenera (49px * 2)
                const availableHeight = viewportHeight - headerHeight - toolbarHeight - containerMargin
                
                if (visibleWidth > 0 && availableHeight > 0) {
                    // Oblicz minimalną szerokość potrzebną do wyświetlenia wszystkich stref
                    const minMapWidth = Math.max(visibleWidth, this.getMinMapWidth())
                    
                    // Oblicz wysokość na podstawie aspect ratio 16:9
                    const heightFromAspectRatio = minMapWidth * 0.5625
                    
                    // Użyj większej wartości: dostępna wysokość lub wysokość z aspect ratio
                    const calculatedHeight = Math.max(400, Math.min(availableHeight, heightFromAspectRatio))
                    
                    // Jeśli wysokość jest ograniczona przez dostępną przestrzeń, dostosuj szerokość
                    if (calculatedHeight < heightFromAspectRatio) {
                        this.containerWidth = Math.max(minMapWidth, calculatedHeight / 0.5625)
                    } else {
                        this.containerWidth = minMapWidth
                    }
                    
                    this.containerHeight = calculatedHeight
                } else {
                    // Jeśli wrapper jeszcze nie ma wymiarów, ustaw domyślne wartości
                    this.containerWidth = 1200
                    this.containerHeight = Math.max(400, availableHeight || 675)
                }
            } else {
                // Jeśli wrapper nie istnieje jeszcze, ustaw domyślne wartości
                this.containerWidth = 1200
                this.containerHeight = 675
            }
        },
        getMinMapWidth() {
            // Oblicz minimalną szerokość potrzebną do wyświetlenia wszystkich stref
            if (this.zones.length === 0) {
                return 0
            }
            let maxRight = 0
            let maxBottom = 0
            this.zones.forEach(zone => {
                const right = zone.x + zone.w
                const bottom = zone.y + zone.h
                if (right > maxRight) {
                    maxRight = right
                }
                if (bottom > maxBottom) {
                    maxBottom = bottom
                }
            })
            // Dodaj margines, aby strefy nie były przy samej krawędzi
            // Użyj większej wartości z szerokości i wysokości (przeliczonej na szerokość)
            const widthFromHeight = maxBottom / 0.5625 // odwrotność aspect ratio 16:9
            return Math.max(maxRight + 50, widthFromHeight + 50)
        },
        toggleEditMode() {
            if (this.isEditDisabled) return // Zablokuj edycję gdy sessionId > 0
            this.isEditMode = !this.isEditMode
            // Odznacz strefę i mikrofon przy wyjściu z trybu edycji
            if (!this.isEditMode) {
                this.selectedZoneId = null
                this.stopEditZone()
                this.selectedMicrophoneId = null
                this.cancelEditMicrophoneNumber(false)
                this.$emit('microphone-selected', null)
            }
            // Emituj zmianę trybu edycji do parent
            this.$emit('edit-mode-changed', this.isEditMode)
        },
        async addZone() {
            if (!this.isEditMode || !this.roomMapId) return
            const newZone = {
                name: `Strefa ${this.zones.length + 1}`,
                x: 50,
                y: 50,
                w: 200,
                h: 100,
                attributes: {
                    backgroundColor: '#ffffff',
                    borderColor: '#004dcc'
                }
            }
            try {
                this.showLoader = true;
                const tempZone = {
                    id: this.tempZoneId++,
                    ...newZone
                }
                this.zones.push(tempZone)
                this.selectZone(tempZone.id)

                const response = await roomMapService.addZone(this.roomMapId, newZone)
                if (response && response.status === 200 && response.data) {
                    const index = this.zones.findIndex(z => z.id === tempZone.id)
                    if (index !== -1) {
                        this.zones[index] = {
                            ...response.data,
                            attributes: response.data.attributes || {
                                backgroundColor: '#ffffff',
                                borderColor: '#004dcc'
                            }
                        }
                    }
                }

                await this.$nextTick();
                this.updateContainerHeight();

            } catch (error) {
                const index = this.zones.findIndex(z => z.id === this.tempZoneId - 1)
                if (index !== -1) {
                    this.zones.splice(index, 1)
                }
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
            }
            finally {
                this.showLoader = false;
            }
        },
        async removeZone(zoneId) {
            if (!this.isEditMode || !this.roomMapId) return
            const index = this.zones.findIndex(z => z.id === zoneId)
            if (index === -1) return
            
            const zoneToRemove = this.zones[index]
            
            try {
                this.showLoader = true;
                this.zones.splice(index, 1)
                if (this.selectedZoneId === zoneId) {
                    this.selectedZoneId = null
                }

                await roomMapService.deleteZone(this.roomMapId, zoneId)
            } catch (error) {
                this.zones.splice(index, 0, zoneToRemove)
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
            }
            finally {
                this.showLoader = false;
            }
        },
        selectZone(zoneId) {
            this.selectedZoneId = zoneId
            this.stopEditZone()
        },
        handleZoneClick(zoneId, event) {
            if (event && event.stopPropagation) {
                event.stopPropagation()
            }
            this.selectZone(zoneId)
        },
        handleZoneMouseDown(zoneId, event) {
            if (event.target && !event.target.classList.contains('vdr-handle')) {
                this.selectZone(zoneId)
            }
        },
        selectMicrophone(micId) {
            if (this.isEditMode) {
                this.selectedMicrophoneId = micId
                this.$emit('microphone-selected', micId)
                this.cancelEditMicrophoneNumber(false)
            } else {
                // W trybie nie-edycji, kliknięcie w mikrofon obsługuje handleMicrophoneClick
                // Ta metoda jest wywoływana przez Vue3DraggableResizable, więc ignorujemy ją
            }
        },
        handleContainerClick(event) {
            const target = event.target
            const currentTarget = event.currentTarget
            
            if (target.closest('.vdr') || target.closest('.zone-content') || target.closest('.mic-on-map') || target.closest('.mic-on-map__users-list')) {
                return // Nie odznaczaj, jeśli kliknięto na strefie, mikrofonie lub liście użytkowników
            }
            
            if (target === currentTarget || !target.closest('.vdr')) {
                this.selectedZoneId = null
                this.stopEditZone()
                this.selectedMicrophoneId = null
                this.expandedMicrophoneId = null // Zamknij listę użytkowników
                this.cancelEditMicrophoneNumber(false)
                if (this.isEditMode) {
                    this.$emit('microphone-selected', null)
                }
            }
        },
        onDragStart(zoneId, event) {
            this.isDraggingZone = true
            this.pendingZoneUpdate = { zoneId, type: 'drag' }

            this.handleDragScroll(event)
        },
        onDrag(zoneId, event) {
            const zoneIndex = this.zones.findIndex(z => z.id === zoneId)
            if (zoneIndex !== -1 && event) {
                const newX = typeof event.x === 'number' ? event.x : (this.zones[zoneIndex].x || 0)
                const newY = typeof event.y === 'number' ? event.y : (this.zones[zoneIndex].y || 0)
                this.zones[zoneIndex].x = newX
                this.zones[zoneIndex].y = newY

                if (this.pendingZoneUpdate && this.pendingZoneUpdate.zoneId === zoneId) {
                    this.pendingZoneUpdate.x = newX
                    this.pendingZoneUpdate.y = newY
                    this.pendingZoneUpdate.w = this.zones[zoneIndex].w
                    this.pendingZoneUpdate.h = this.zones[zoneIndex].h
                }
            }
        },
        onResizeStart(zoneId, event) {
            this.isResizingZone = true
            this.pendingZoneUpdate = { zoneId, type: 'resize' }

            this.handleDragScroll(event)
        },
        onResize(zoneId, event) {
            const zoneIndex = this.zones.findIndex(z => z.id === zoneId)
            if (zoneIndex !== -1 && event) {
                const newX = typeof event.x === 'number' ? event.x : (this.zones[zoneIndex].x || 0)
                const newY = typeof event.y === 'number' ? event.y : (this.zones[zoneIndex].y || 0)
                const newW = typeof event.w === 'number' ? event.w : (this.zones[zoneIndex].w || 0)
                const newH = typeof event.h === 'number' ? event.h : (this.zones[zoneIndex].h || 0)
                this.zones[zoneIndex].x = newX
                this.zones[zoneIndex].y = newY
                this.zones[zoneIndex].w = newW
                this.zones[zoneIndex].h = newH

                if (this.pendingZoneUpdate && this.pendingZoneUpdate.zoneId === zoneId) {
                    this.pendingZoneUpdate.x = newX
                    this.pendingZoneUpdate.y = newY
                    this.pendingZoneUpdate.w = newW
                    this.pendingZoneUpdate.h = newH
                }
            }
        },
        async onResizeStop(zoneId, event) {
            this.isResizingZone = false

            this.stopDragScroll()
            
            const zoneIndex = this.zones.findIndex(z => z.id === zoneId)
            if (zoneIndex === -1 || !this.roomMapId) {
                this.pendingZoneUpdate = null
                return
            }
            
            const newX = event && typeof event.x === 'number' ? event.x : ((this.pendingZoneUpdate?.x ?? this.zones[zoneIndex].x) || 0)
            const newY = event && typeof event.y === 'number' ? event.y : ((this.pendingZoneUpdate?.y ?? this.zones[zoneIndex].y) || 0)
            const newW = event && typeof event.w === 'number' ? event.w : ((this.pendingZoneUpdate?.w ?? this.zones[zoneIndex].w) || 0)
            const newH = event && typeof event.h === 'number' ? event.h : ((this.pendingZoneUpdate?.h ?? this.zones[zoneIndex].h) || 0)
            
            this.zones[zoneIndex].x = newX
            this.zones[zoneIndex].y = newY
            this.zones[zoneIndex].w = newW
            this.zones[zoneIndex].h = newH
            
            const zone = this.zones[zoneIndex]
            try {
                await roomMapService.updateZone(this.roomMapId, zone.id, {
                    x: zone.x,
                    y: zone.y,
                    w: zone.w,
                    h: zone.h,
                    name: zone.name,
                    attributes: zone.attributes
                })
            } catch (error) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
            }
            
            this.pendingZoneUpdate = null
            
            await this.$nextTick();
            this.updateContainerHeight();
           
        },
        getNextMicrophoneNumber() {
            const usedNumbers = new Set(this.microphones.map(m => Number(m.number)))
            let candidate = 1
            while (usedNumbers.has(candidate)) {
                candidate++
            }
            return candidate
        },
        isMicNumberUnique(number, excludeId = null) {
            return !this.microphones.some(m => Number(m.number) === number && m.id !== excludeId)
        },
        showMicrophoneError(message) {
            var toast = this.$swal.mixin(AlertHelper.errorToastElement())
            toast.fire(AlertHelper.errorToastAlert(message, this.$t('com.global.error')))
        },
        async addMicrophone() {
            if (!this.isEditMode || !this.roomMapId) return
            const tempId = this.tempMicrophoneId--
            const newMic = {
                id: tempId,
                number: this.getNextMicrophoneNumber(),
                x: 80,
                y: 80
            }
            this.microphones.push(newMic)
            this.selectedMicrophoneId = tempId
            try {
                this.showLoader = true;
                const response = await roomMapService.addMicrophone(this.roomMapId, {
                    number: newMic.number,
                    x: newMic.x,
                    y: newMic.y
                })
                if (response?.status === 200 && response.data) {
                    const index = this.microphones.findIndex(m => m.id === tempId)
                    if (index !== -1) {
                        this.microphones[index] = {
                            id: response.data.id,
                            number: response.data.number ?? response.data.Number,
                            x: response.data.x ?? response.data.X,
                            y: response.data.y ?? response.data.Y
                        }
                        this.selectedMicrophoneId = this.microphones[index].id
                    }
                }
            } catch (error) {
                this.microphones = this.microphones.filter(m => m.id !== tempId)
                this.showMicrophoneError(this.$t('com.global.default-save-error'))
            }
            finally {
                this.showLoader = false;
            }
        },
        async removeMicrophone(micId) {
            if (!this.isEditMode || !this.roomMapId) return
            
            // Sprawdź czy mikrofon ma przypisanych użytkowników
            const assignedUsers = this.getMicrophoneUsers(micId)
            if (assignedUsers && assignedUsers.length > 0) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(
                    this.$t('com.file.room-map.cannot-delete-microphone-with-users') || 'Nie można usunąć mikrofonu z przypisanymi użytkownikami',
                    this.$t('com.global.error')
                ));
                return
            }
            
            const index = this.microphones.findIndex(m => m.id === micId)
            if (index === -1) return
            const removed = this.microphones[index]
            this.microphones.splice(index, 1)
            if (this.selectedMicrophoneId === micId) {
                this.selectedMicrophoneId = null
            }
            try {
                this.showLoader = true;
                if (micId > 0) {
                    await roomMapService.deleteMicrophone(this.roomMapId, micId)
                }
            } catch (error) {
                this.microphones.splice(index, 0, removed)
                this.showMicrophoneError(this.$t('com.global.default-save-error'))
            }
            finally {
                this.showLoader = false;
            }
        },
        async onDragStop(zoneId, event) {
            this.isDraggingZone = false

            this.stopDragScroll()
            
            const zoneIndex = this.zones.findIndex(z => z.id === zoneId)
            if (zoneIndex === -1 || !this.roomMapId) {
                this.pendingZoneUpdate = null
                return
            }
            
            const newX = event && typeof event.x === 'number' ? event.x : ((this.pendingZoneUpdate?.x ?? this.zones[zoneIndex].x) || 0)
            const newY = event && typeof event.y === 'number' ? event.y : ((this.pendingZoneUpdate?.y ?? this.zones[zoneIndex].y) || 0)
            
            this.zones[zoneIndex].x = newX
            this.zones[zoneIndex].y = newY
            
            const zone = this.zones[zoneIndex]
            try {
                await roomMapService.updateZone(this.roomMapId, zone.id, {
                    x: zone.x,
                    y: zone.y,
                    w: zone.w,
                    h: zone.h,
                    name: zone.name,
                    attributes: zone.attributes
                })
            } catch (error) {
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
            }
            
            this.pendingZoneUpdate = null

            await this.$nextTick();
            this.updateContainerHeight();
        },
        async startEditZone(zoneId) {
            if (!this.isEditMode) return

            const zone = this.zones.find(z => z.id === zoneId)
            if (!zone) return

            this.selectedZoneId = zoneId
            this.editingZoneId = zoneId
            this.originalZoneName = zone.name

            await this.$nextTick()

            const input = this.$refs.zoneNameInput
            const el = Array.isArray(input) ? input[0] : input
            if (el) {
                el.focus()
                el.select?.()
            }
        },
        async stopEditZone() {
            if (this.editingZoneId && this.roomMapId) {
                const zone = this.zones.find(z => z.id === this.editingZoneId)
                if (zone) {
                    try {
                        await roomMapService.updateZone(this.roomMapId, zone.id, {
                            x: zone.x,
                            y: zone.y,
                            w: zone.w,
                            h: zone.h,
                            name: zone.name,
                            attributes: zone.attributes
                        })
                    } catch (error) {
                        var t = this.$swal.mixin(AlertHelper.errorToastElement());
                        t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
                    }
                }
            }
            this.editingZoneId = null
            this.originalZoneName = ''
        },
        cancelEditZone() {
            if (this.editingZoneId) {
                const zone = this.zones.find(z => z.id === this.editingZoneId)
                if (zone) {
                    zone.name = this.originalZoneName
                }
            }
            this.stopEditZone()
        },
        async startEditMicrophoneNumber(micId) {
            if (!this.isEditMode) return

            const mic = this.microphones.find(m => m.id === micId)
            if (!mic) return

            this.editingMicrophoneId = micId
            this.micOriginalNumber = mic.number

            await this.$nextTick()

            const ref = this.$refs[`mic-input-${micId}`]
            const el = Array.isArray(ref) ? ref[0] : ref

            if (el?.focus) {
                el.focus()
                el.select?.()
            }
        },
        cancelEditMicrophoneNumber(resetValue = true) {
            if (resetValue && this.editingMicrophoneId) {
                const mic = this.microphones.find(m => m.id === this.editingMicrophoneId)
                if (mic && this.micOriginalNumber !== null) {
                    mic.number = this.micOriginalNumber
                }
            }
            this.editingMicrophoneId = null
            this.micOriginalNumber = null
        },
        async saveMicrophoneNumber(micId) {
            const mic = this.microphones.find(m => m.id === micId)
            if (!mic) {
                this.cancelEditMicrophoneNumber(false)
                return
            }
            const parsedNumber = parseInt(mic.number, 10)
            if (!Number.isInteger(parsedNumber) || parsedNumber <= 0) {
                this.showMicrophoneError(this.$t('com.file.room-map.microphone-number-invalid') || 'Numer mikrofonu musi być dodatnią liczbą całkowitą')
                this.cancelEditMicrophoneNumber(true)
                return
            }
            if (!this.isMicNumberUnique(parsedNumber, micId)) {
                this.showMicrophoneError(this.$t('com.file.room-map.microphone-number-duplicate') || 'Numer mikrofonu musi być unikalny')
                this.cancelEditMicrophoneNumber(true)
                return
            }
            mic.number = parsedNumber
            const originalNumber = this.micOriginalNumber
            this.cancelEditMicrophoneNumber(false)
            if (!this.roomMapId || mic.id <= 0) {
                return
            }
            try {
                this.showLoader = true;
                await roomMapService.updateMicrophone(this.roomMapId, mic.id, {
                    id: mic.id,
                    number: mic.number,
                    x: mic.x,
                    y: mic.y
                })
            } catch (error) {
                mic.number = originalNumber
                this.showMicrophoneError(this.$t('com.global.default-save-error'))
            } finally {
                this.cancelEditMicrophoneNumber(false);
                this.showLoader = false;
            }
        },
        getZonePosition(zone) {
            if (!zone) return '0, 0'
            const x = (typeof zone.x === 'number' && !isNaN(zone.x)) ? zone.x : 0
            const y = (typeof zone.y === 'number' && !isNaN(zone.y)) ? zone.y : 0
            return `${Math.round(x)}, ${Math.round(y)}`
        },
        getZoneSize(zone) {
            if (!zone) return '0 × 0'
            const w = (typeof zone.w === 'number' && !isNaN(zone.w)) ? zone.w : 0
            const h = (typeof zone.h === 'number' && !isNaN(zone.h)) ? zone.h : 0
            return `${Math.round(w)} × ${Math.round(h)}`
        },
        getCornerCoords(zone, corner) {
            if (!zone) return '0, 0'
            
            const x = typeof zone.x === 'number' && !isNaN(zone.x) ? zone.x : 0
            const y = typeof zone.y === 'number' && !isNaN(zone.y) ? zone.y : 0
            const w = typeof zone.w === 'number' && !isNaN(zone.w) ? zone.w : 0
            const h = typeof zone.h === 'number' && !isNaN(zone.h) ? zone.h : 0
            
            let coordX = 0
            let coordY = 0
            
            switch (corner) {
                case 'top-left':
                    coordX = x
                    coordY = y
                    break
                case 'top-right':
                    coordX = x + w
                    coordY = y
                    break
                case 'bottom-left':
                    coordX = x
                    coordY = y + h
                    break
                case 'bottom-right':
                    coordX = x + w
                    coordY = y + h
                    break
                default:
                    return '0, 0'
            }
            
            return `${Math.round(coordX)}, ${Math.round(coordY)}`
        },
        onMicrophoneDragStart(micId, event) {
            if (!this.isEditMode) return
            this.isDraggingMicrophone = true
            this.pendingMicrophoneUpdate = { micId }
            this.handleDragScroll(event)
        },
        onMicrophoneDrag(micId, event) {
            if (!this.isEditMode || !event) return
            const mic = this.microphones.find(m => m.id === micId)
            if (!mic) return
            const newX = typeof event.x === 'number' ? Math.round(event.x) : mic.x
            const newY = typeof event.y === 'number' ? Math.round(event.y) : mic.y
            mic.x = newX
            mic.y = newY
            if (this.pendingMicrophoneUpdate && this.pendingMicrophoneUpdate.micId === micId) {
                this.pendingMicrophoneUpdate.x = newX
                this.pendingMicrophoneUpdate.y = newY
            }
        },
        async onMicrophoneDragStop(micId, event) {
            if (!this.isEditMode) return
            this.isDraggingMicrophone = false
            this.stopDragScroll()
            const mic = this.microphones.find(m => m.id === micId)
            if (!mic || !this.roomMapId) {
                this.pendingMicrophoneUpdate = null
                return
            }
            const newX = event && typeof event.x === 'number' ? Math.round(event.x) : Math.round((this.pendingMicrophoneUpdate?.x ?? mic.x) || 0)
            const newY = event && typeof event.y === 'number' ? Math.round(event.y) : Math.round((this.pendingMicrophoneUpdate?.y ?? mic.y) || 0)
            mic.x = newX
            mic.y = newY
            if (mic.id > 0) {
                try {
                    await roomMapService.updateMicrophone(this.roomMapId, mic.id, {
                        id: mic.id,
                        number: mic.number,
                        x: mic.x,
                        y: mic.y
                    })
                } catch (error) {
                    this.showMicrophoneError(this.$t('com.global.default-save-error'))
                }
            }
            this.pendingMicrophoneUpdate = null
        },
        startVoting() {
            if (this.isControlDisabled) return;

            if (this.getInteractiveVotingModalInAppStatus === true) return;

            if (this.votingModule === 1) {
                const agendaId = this.activePoint && this.activePoint.id > 0 ? this.activePoint.id : null;
                this.$emit('start-voting', agendaId)
            }
        },
        async updateZoneBackgroundColor(color) {
            if (!this.selectedZoneId || !this.roomMapId) return

            let normalizedColor = color.trim()
            if (normalizedColor.startsWith('#')) {
                if (normalizedColor.length === 4) {
                    // Konwertuj #RGB na #RRGGBB
                    normalizedColor = '#' + normalizedColor[1] + normalizedColor[1] + 
                                     normalizedColor[2] + normalizedColor[2] + 
                                     normalizedColor[3] + normalizedColor[3]
                }
            } else {
                normalizedColor = '#' + normalizedColor
            }
            // Walidacja koloru hex
            if (/^#([A-Fa-f0-9]{6})$/.test(normalizedColor)) {
                const zone = this.zones.find(z => z.id === this.selectedZoneId)
                if (zone) {
                    if (!zone.attributes) {
                        zone.attributes = {}
                    }
                    zone.attributes.backgroundColor = normalizedColor
                    
                    try {
                        this.showLoader = true;
                        await roomMapService.updateZone(this.roomMapId, zone.id, {
                            x: zone.x,
                            y: zone.y,
                            w: zone.w,
                            h: zone.h,
                            name: zone.name,
                            attributes: zone.attributes
                        })
                    } catch (error) {
                        var t = this.$swal.mixin(AlertHelper.errorToastElement());
                        t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
                    }
                    finally {
                        this.showLoader = false;
                    }
                }
            }
        },
        handleDragScroll(event) {
            event.preventDefault();
            // Sprawdź, czy przeciąganie jest aktywne
            if (!this.isEditMode) return
            
            const wrapper = this.$refs.mapWrapper
            if (!wrapper) return
            
            // Zatrzymaj poprzedni interval, jeśli istnieje
            if (this.dragScrollInterval) {
                clearInterval(this.dragScrollInterval)
                this.dragScrollInterval = null
            }
            
            // Użyj globalnego event listenera do śledzenia pozycji myszy
            const handleMouseMove = (e) => {
                const rect = wrapper.getBoundingClientRect()
                const scrollThreshold = 50 // Odległość od krawędzi, przy której zaczyna się scrollowanie
                const scrollSpeed = 10 // Szybkość scrollowania
                
                // Sprawdź, czy kursor jest blisko krawędzi
                const nearLeft = e.clientX - rect.left < scrollThreshold
                const nearRight = rect.right - e.clientX < scrollThreshold
                const nearTop = e.clientY - rect.top < scrollThreshold
                const nearBottom = rect.bottom - e.clientY < scrollThreshold
                
                // Oblicz, w którą stronę scrollować
                let scrollX = 0
                let scrollY = 0
                
                if (nearLeft) {
                    scrollX = -scrollSpeed
                } else if (nearRight) {
                    scrollX = scrollSpeed
                }
                
                if (nearTop) {
                    scrollY = -scrollSpeed
                } else if (nearBottom) {
                    scrollY = scrollSpeed
                }
                
                // Wykonaj scrollowanie
                if (scrollX !== 0 || scrollY !== 0) {
                    wrapper.scrollBy({
                        left: scrollX,
                        top: scrollY,
                        behavior: 'auto'
                    })
                }
            }
            
            // Dodaj globalny event listener
            document.addEventListener('mousemove', handleMouseMove)
            
            // Zapisz funkcję do usunięcia listenera
            this.dragScrollMouseMoveHandler = handleMouseMove
        },
        stopDragScroll() {
            if (this.dragScrollInterval) {
                clearInterval(this.dragScrollInterval)
                this.dragScrollInterval = null
            }
            // Usuń globalny event listener
            if (this.dragScrollMouseMoveHandler) {
                document.removeEventListener('mousemove', this.dragScrollMouseMoveHandler)
                this.dragScrollMouseMoveHandler = null
            }
        },
        async handleGlobalMouseUp(event) {
            event.preventDefault();
            // Fallback: jeśli drag/resize się zakończyło, ale eventy nie zostały wywołane
            if (this.isDraggingZone && this.pendingZoneUpdate) {
                await this.onDragStop(this.pendingZoneUpdate.zoneId, null)
            } else if (this.isResizingZone && this.pendingZoneUpdate) {
                await this.onResizeStop(this.pendingZoneUpdate.zoneId, null)
            } else if (this.isDraggingMicrophone && this.pendingMicrophoneUpdate) {
                await this.onMicrophoneDragStop(this.pendingMicrophoneUpdate.micId, null)
            }
        },
        async updateZoneBorderColor(color) {
            if (!this.selectedZoneId || !this.roomMapId) return
            // Normalizuj kolor (3-znakowy hex na 6-znakowy)
            let normalizedColor = color.trim()
            if (normalizedColor.startsWith('#')) {
                if (normalizedColor.length === 4) {
                    // Konwertuj #RGB na #RRGGBB
                    normalizedColor = '#' + normalizedColor[1] + normalizedColor[1] + 
                                     normalizedColor[2] + normalizedColor[2] + 
                                     normalizedColor[3] + normalizedColor[3]
                }
            } else {
                normalizedColor = '#' + normalizedColor
            }
            // Walidacja koloru hex
            if (/^#([A-Fa-f0-9]{6})$/.test(normalizedColor)) {
                const zone = this.zones.find(z => z.id === this.selectedZoneId)
                if (zone) {
                    if (!zone.attributes) {
                        zone.attributes = {}
                    }
                    zone.attributes.borderColor = normalizedColor
                    
                    try {
                        this.showLoader = true;
                        await roomMapService.updateZone(this.roomMapId, zone.id, {
                            x: zone.x,
                            y: zone.y,
                            w: zone.w,
                            h: zone.h,
                            name: zone.name,
                            attributes: zone.attributes
                        })
                    } catch (error) {
                        var t = this.$swal.mixin(AlertHelper.errorToastElement());
                        t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
                    }
                    finally {
                        this.showLoader = false;
                    }
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/roommap.scss';
</style>

